import React from 'react';
import { Shield, X } from 'lucide-react';
import './PrivacyPolicyDrawer.css';

export function PrivacyPolicyDrawer({ isOpen, onClose }) {
  if (!isOpen) return null;

  return (
    <>
      <div className="privacy-drawer-overlay" onClick={onClose}></div>
      <div className="privacy-drawer">
        <div className="privacy-drawer-header">
          <div className="privacy-drawer-title">
            <Shield size={20} style={{ color: "#000080" }} />
            <span>Privacy & Policy</span>
          </div>
          <button className="privacy-drawer-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="privacy-drawer-content">
          <section className="privacy-section">
            <h3>DATA WE COLLECT</h3>
            <p>SmartCargo collects only the data necessary to operate the platform effectively and securely. This includes:</p>

            <div className="privacy-item">
              <strong>Personal Information:</strong> Full name, email address, phone number, and role (Admin, Courier) provided during account registration.
            </div>

            <div className="privacy-item">
              <strong>Delivery & Package Data:</strong> Shipment records, package status history, assigned courier details, warehouse origin and destination points, and timestamps of all delivery events.
            </div>

            <div className="privacy-item">
              <strong>Location Data:</strong> Real-time GPS coordinates of couriers — collected exclusively during active delivery sessions. Location tracking begins when a courier marks a delivery as started and ends automatically upon delivery completion or session logout.
            </div>

            <div className="privacy-item">
              <strong>Performance & Rating Data:</strong> Delivery success rates, average delivery times, customer ratings, and delay records attributed to individual courier accounts.
            </div>

            <div className="privacy-item">
              <strong>System Usage Data:</strong> Login timestamps, device type, browser information, and session activity logs for security and audit purposes.
            </div>
          </section>

          <section className="privacy-section">
            <h3>HOW WE USE YOUR DATA</h3>
            <p>All data collected within SmartCargo is used strictly for the following operational purposes:</p>

            <div className="privacy-item">
              <strong>Route Optimization:</strong> Location and traffic data are processed in real time to calculate the most efficient delivery routes for couriers, reducing delivery time and fuel usage.
            </div>

            <div className="privacy-item">
              <strong>Delivery Tracking:</strong> Package status data is continuously updated to provide administrators and relevant stakeholders with accurate, real-time visibility into every shipment.
            </div>

            <div className="privacy-item">
              <strong>Courier Performance Evaluation:</strong> Delivery metrics and customer ratings are aggregated to generate fair and objective performance reports. These reports are used for operational improvement and task allocation.
            </div>
          </section>

          <section className="privacy-section">
            <h3>LOCATION TRACKING</h3>
            <p>Location tracking is fundamental to SmartCargo's operation. We process location data with strict constraints:</p>
            <ul>
              <li><strong>Session-Only Tracking:</strong> GPS tracking is active only when a courier is actively signed into the app and marked as "Available" or "On Delivery".</li>
              <li><strong>Battery Efficiency:</strong> Our tracking algorithms balance real-time accuracy with battery preservation.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h3>DATA STORAGE & SECURITY</h3>
            <p>We implement robust technical and organizational measures to protect your data:</p>
            <ul>
              <li><strong>Encryption:</strong> All data transmitted between the mobile application, web dashboard, and our servers is encrypted using industry-standard TLS.</li>
              <li><strong>Access Control:</strong> Administrative access to sensitive data is strictly governed by role-based access controls (RBAC).</li>
              <li><strong>Audits:</strong> We conduct regular security audits and vulnerability assessments.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h3>DATA SHARING & THIRD PARTIES</h3>
            <p>SmartCargo does not sell, rent, or trade your personal information. We may share data only under the following circumstances:</p>
            <ul>
              <li><strong>Service Providers:</strong> We may employ third-party companies and individuals to facilitate our service (e.g., cloud hosting providers, map APIs). These third parties have access to your personal data only to perform these tasks on our behalf and are obligated not to disclose or use it for any other purpose.</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h3>YOUR RIGHTS</h3>
            <p>As a user of SmartCargo, you have the following rights regarding your personal data:</p>
            <ul>
              <li><strong>Access:</strong> The right to request copies of your personal data.</li>
              <li><strong>Rectification:</strong> The right to request that we correct any information you believe is inaccurate.</li>
              <li><strong>Erasure:</strong> The right to request that we erase your personal data, under certain conditions.</li>
              <li><strong>Restriction:</strong> The right to request that we restrict the processing of your personal data, under certain conditions.</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h3>DATA RETENTION</h3>
            <p>We retain your personal data only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your data to the extent necessary to comply with our legal obligations, resolve disputes, and enforce our legal agreements and policies.</p>
          </section>

          <section className="privacy-section">
            <h3>CONTACT</h3>
            <p>If you have any questions or suggestions about our Privacy Policy, do not hesitate to contact us at:</p>
            <p><strong>Email:</strong> privacy@smartcargo.az</p>
          </section>
        </div>

        <div className="privacy-drawer-footer">
          <p>Version 1.2</p>
          <p>Last Updated: October 2024</p>
        </div>
      </div>
    </>
  );
}
